import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

import { Refresh } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { deepCopyObject } from "../../utils/helpers.js";
import AlertDialog from "../dialogs//alertDialog";
import BotpressBulkFeedbackDialog from "../dialogs/botpressBulkFeedbackDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressFeedbackDialog from "../dialogs/botpressUserFeedbackDialog.js";
import BotpressFeedbackTable from "../ui/botpressFeedbackTable.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressUserFeedback = props => {
  const {
    feedback,
    searchFeedbackRequest,
    deleteChatRequest,
    feedbackPagination,
    loadConversationRequest,
    setRerunConversationIdRequest,
    deleteFeedbackRequest,
    botODM,
    updateFeedbackRequest,
    widgetSize = "large",
    minHeight = "333px",
  } = props;

  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);

  useEffect(() => {
    if (feedback != null) {
      setRows(feedback);
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  const onRequestNewPage = async pagination => {
    try {
      searchFeedbackRequest(pagination);
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  useEffect(() => {
    if (selectedConversation != null) {
      loadConversationRequest(selectedConversation.conversationId);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  const onHandleDeveloperLogs = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      const theURL = `https://app.botpress.cloud/workspaces/${botODM.omniData.workspaceId}/bots/${botODM.omniData.botId}/conversations/${conversationId}/messages/${conversationRow.messageId} `;
      window.open(theURL, "_blank");

      //got to page.
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  const [deleteRows, setDeleteRows] = useState(null);

  const onDeleteFeedback = rowsIDs => {
    try {
      if (rowsIDs == null) {
        return;
      }
      setDeleteRows(rowsIDs);
    } catch (error) {
      console.error("Error deleting table rows", error);
      setRows([]);
    }
  };

  const [editRows, setEditRows] = useState(null);

  const onBulkEditFeedback = rows => {
    try {
      if (rows == null) {
        return;
      }
      setEditRows(rows);
    } catch (error) {
      console.error("Error editing table rows", error);
      setRows([]);
    }
  };

  const onRerunConversation = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      let conversationNumber = conversationRow.conversationNumber;
      let feedbackNumber = conversationRow.id;
      setRerunConversationIdRequest({
        rerunConversationId: conversationId,
        rerunConversationNumber: conversationNumber.toString(),
        rerunFeedbackNumber: feedbackNumber,
      });
    } catch (error) {
      console.error("Error getting table rows", error);
    }
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle title={"Feedback"} text={`${feedbackPagination.totalItems} User Comments`}>
        <OmniResponsiveButton
          icon={<Refresh />}
          tooltipTitle="Load this table again"
          buttonText="Refresh"
          onClick={() => {
            let newPagination = deepCopyObject(feedbackPagination);
            newPagination["invalidateCache"] = true;
            onRequestNewPage(newPagination);
          }}
        />
      </WidgetTitle>

      <WidgetSurveyContent>
        <BotpressFeedbackTable
          data={rows}
          onEditEntry={setSelectedEntry}
          onRequestNewPage={onRequestNewPage}
          dataReduxPagination={feedbackPagination}
          onViewConversation={setSelectedConversation}
          totalItemCount={feedbackPagination.totalItems}
          onHandleDeveloperLogs={onHandleDeveloperLogs}
          onRerunConversation={onRerunConversation}
          onDeleteFeedback={onDeleteFeedback}
          onBulkEditFeedback={onBulkEditFeedback}
        />
      </WidgetSurveyContent>

      {selectedEntry != null && (
        <BotpressFeedbackDialog
          open={selectedEntry != null}
          userFeedbackRecord={selectedEntry}
          onAccept={theRecord => {
            updateFeedbackRequest({ feedbackRecords: [theRecord] });
            setSelectedEntry(null);
          }}
          onDelete={theRecord => {
            props.deleteFeedbackRequest({ feedbackIDs: [theRecord.id] });
            setSelectedEntry(null);
          }}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedConversation != null && (
        <BotpressConversationDialog
          conversationId={selectedConversation.conversationId}
          conversationNumber={selectedConversation.conversationNumber}
          conversation={props.conversation}
          dialogTitle={`Conversation #${selectedConversation.conversationNumber}`}
          conversationFeedback={props.conversationFeedback}
          deleteFeedbackRequest={props.deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          messageId={selectedConversation.messageId}
          userODM={props.userODM}
          open={selectedConversation != null}
          defaultValue={selectedConversation}
          onAccept={() => {
            loadConversationRequest(null);
            setSelectedConversation(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setSelectedConversation(null);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: selectedConversation.id });
            loadConversationRequest(null);
            setSelectedConversation(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
          }}
        />
      )}

      {deleteRows && (
        <AlertDialog
          open={deleteRows}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete these Comments?"}
          onAccept={() => {
            deleteFeedbackRequest({ feedbackIDs: deleteRows });
            setDeleteRows(null);
          }}
          onClose={() => {
            setDeleteRows(null);
          }}
        />
      )}

      {editRows && (
        <BotpressBulkFeedbackDialog
          open={editRows}
          dialogTitle={`Bulk Edit`}
          dialogMessage={"Are you sure you want to edit these Comments?"}
          onAccept={updates => {
            // let theUpdates = {
            //   status: status,
            //   archived: archived,
            //   response: response,
            // };
            for (let i = 0; i < editRows.length; i++) {
              editRows[i].status = updates.status;
              editRows[i].archived = updates.archived;
              editRows[i].response = editRows[i].response
                ? editRows[i].response + " - " + updates.response
                : updates.response;
            }
            updateFeedbackRequest({ feedbackRecords: editRows });
            setEditRows(null);
          }}
          onClose={() => {
            setEditRows(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  feedback: state.botpressSession.feedback,
  feedbacksStartDate: state.botpressSession.feedbacksStartDate,
  feedbacksEndDate: state.botpressSession.feedbacksEndDate,
  feedbackPagination: state.botpressSession.feedbackPagination,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,

  userODM: state.userSession.userODM,

  botODM: state.botpressSession.botpressBotODM,
});

const mapDispatchToProps = {
  searchFeedbackRequest: BotpressSessionActions.searchFeedbackRequest,
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  setRerunConversationIdRequest: BotpressSessionActions.setRerunConversationIdRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressUserFeedback);
